package com.fantasytenis.web.components

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.scoring.entity.LiveMatchStatus
import com.fantasytenis.shared.domain.scoring.entity.LiveScoreEntity
import com.fantasytenis.shared.domain.tennis.entity.MatchEntity
import com.fantasytenis.shared.domain.tennis.entity.PeriodScoreEntity
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.ThemeColors
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Auto-rotating carousel ticker for displaying multiple live matches.
 *
 * Features:
 * - Auto-rotates through matches every [rotationIntervalMs] milliseconds
 * - Pauses rotation on hover
 * - Shows position counter (1/5) and navigation arrows for 2+ matches
 * - Crossfade animation between matches (respects prefers-reduced-motion)
 * - Falls back to simple display for single match
 */
@Composable
fun LiveMatchTicker(
    matches: List<MatchEntity>,
    liveScores: Map<String, LiveScoreEntity>,
    colors: ThemeColors,
    rotationIntervalMs: Long = 4000
) {
    if (matches.isEmpty()) return

    var currentIndex by remember { mutableStateOf(0) }
    var isHovered by remember { mutableStateOf(false) }
    var opacity by remember { mutableStateOf(1.0) }

    // Reset index if matches list changes
    LaunchedEffect(matches.size) {
        if (currentIndex >= matches.size) {
            currentIndex = 0
        }
    }

    // Auto-rotation with crossfade
    LaunchedEffect(currentIndex, isHovered, matches.size) {
        if (!isHovered && matches.size > 1) {
            delay(rotationIntervalMs)
            // Fade out
            opacity = 0.0
            delay(150) // Half of transition duration
            // Update index
            currentIndex = (currentIndex + 1) % matches.size
            // Fade in
            opacity = 1.0
        }
    }

    val currentMatch = matches.getOrNull(currentIndex) ?: return
    val liveScore = liveScores[currentMatch.id]

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
            flex(1)
            minWidth(0.px)
        }
        onMouseEnter { isHovered = true }
        onMouseLeave { isHovered = false }
    }) {
        // Match display with crossfade
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                overflow("hidden")
                flex(1)
                minWidth(0.px)
                opacity(opacity)
                property("transition", "opacity 150ms ease-in-out")
            }
        }) {
            MatchDisplay(
                match = currentMatch,
                liveScore = liveScore,
                colors = colors
            )
        }

        // Counter and navigation (only for 2+ matches)
        if (matches.size > 1) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    flexShrink(0)
                }
            }) {
                // Position counter
                Span({
                    style {
                        fontSize(12.px)
                        color(colors.textSecondary)
                        fontWeight(500)
                        property("font-variant-numeric", "tabular-nums")
                    }
                }) {
                    Text("${currentIndex + 1}/${matches.size}")
                }

                // Navigation arrows
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(4.px)
                    }
                }) {
                    // Previous arrow
                    NavArrow(
                        direction = "prev",
                        colors = colors,
                        onClick = {
                            opacity = 0.0
                            currentIndex = if (currentIndex == 0) matches.size - 1 else currentIndex - 1
                            opacity = 1.0
                        }
                    )

                    // Next arrow
                    NavArrow(
                        direction = "next",
                        colors = colors,
                        onClick = {
                            opacity = 0.0
                            currentIndex = (currentIndex + 1) % matches.size
                            opacity = 1.0
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun MatchDisplay(
    match: MatchEntity,
    liveScore: LiveScoreEntity?,
    colors: ThemeColors
) {
    val p1Name = liveScore?.player1?.playerName ?: match.player1Name ?: "TBD"
    val p2Name = liveScore?.player2?.playerName ?: match.player2Name ?: "TBD"
    val serving = liveScore?.serving
    val scoreDisplay = formatMatchScore(liveScore, match)

    // Player 1
    Span({
        style {
            fontSize(14.px)
            color(colors.textPrimary)
            fontWeight(if (serving == "player1") 600 else 400)
            property("white-space", "nowrap")
            overflow("hidden")
            property("text-overflow", "ellipsis")
        }
    }) {
        Text("$p1Name${if (serving == "player1") "*" else ""}")
    }

    // Score (if available)
    if (!scoreDisplay.isNullOrEmpty()) {
        Span({
            style {
                fontSize(14.px)
                fontWeight(600)
                color(colors.primary)
                flexShrink(0)
                property("font-variant-numeric", "tabular-nums")
            }
        }) {
            Text(scoreDisplay)
        }
    }

    // Player 2
    Span({
        style {
            fontSize(14.px)
            color(colors.textPrimary)
            fontWeight(if (serving == "player2") 600 else 400)
            property("white-space", "nowrap")
            overflow("hidden")
            property("text-overflow", "ellipsis")
        }
    }) {
        Text("${if (serving == "player2") "*" else ""}$p2Name")
    }
}

/**
 * Format match score for ticker display.
 *
 * Output examples:
 * - "6-4 3-2 (40-30)" - Set 1 complete, set 2 in progress at 40-30
 * - "6-4 6-6 (5-3)" - Tiebreak in progress
 * - "2-1 (15-0)" - First set, game in progress
 * - "6-4 6-3" - Match complete (no point score)
 */
private fun formatMatchScore(
    liveScore: LiveScoreEntity?,
    match: MatchEntity
): String? {
    // Try real-time score first
    if (liveScore != null) {
        return formatLiveScore(liveScore)
    }

    // Fall back to match periodScores from REST API
    if (match.periodScores.isNotEmpty()) {
        return formatPeriodScores(match.periodScores)
    }

    return null
}

private fun formatLiveScore(liveScore: LiveScoreEntity): String {
    val p1 = liveScore.player1
    val p2 = liveScore.player2

    return buildString {
        // Completed sets (all but last entry in sets list)
        val completedSetCount = maxOf(0, p1.sets.size - 1)
        for (i in 0 until completedSetCount) {
            if (i > 0) append(" ")
            append("${p1.sets[i]}-${p2.sets[i]}")
        }

        // Current set games
        if (completedSetCount > 0) append(" ")
        append("${p1.games}-${p2.games}")

        // Point score or tiebreak score
        when {
            p1.tiebreakPoints != null && p2.tiebreakPoints != null -> {
                append(" (${p1.tiebreakPoints}-${p2.tiebreakPoints})")
            }
            p1.points.isNotEmpty() && p2.points.isNotEmpty() &&
            liveScore.status == LiveMatchStatus.IN_PROGRESS -> {
                append(" (${p1.points}-${p2.points})")
            }
        }
    }
}

private fun formatPeriodScores(periodScores: List<PeriodScoreEntity>): String {
    return periodScores
        .sortedBy { it.setNumber }
        .joinToString(" ") { "${it.homeScore}-${it.awayScore}" }
}

@Composable
private fun NavArrow(
    direction: String,
    colors: ThemeColors,
    onClick: () -> Unit
) {
    Span({
        style {
            width(24.px)
            height(24.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            borderRadius(4.px)
            cursor("pointer")
            color(colors.textSecondary)
            fontSize(14.px)
            property("user-select", "none")
            property("transition", "background-color 150ms ease, color 150ms ease")
        }
        onClick { onClick() }
        onMouseEnter {
            // Hover effect handled via CSS would be cleaner but this works
        }
    }) {
        Text(if (direction == "prev") "‹" else "›")
    }
}
